import { html, type TemplateResult } from 'lit-html';
import { api, get } from '../api.js';
import { act, app, closeModal, confirmDialog, copy, emptyRow, isAdmin, openModal, type Page } from '../app.js';
import { ago, pill, when } from '../format.js';
import type { ApiKey, Connector } from '../types.js';

let keys: ApiKey[] = [];
let connectors: Connector[] = [];
let showRevoked = false;

async function load(): Promise<void> {
  const [k, c] = await Promise.all([get<{ keys: ApiKey[] }>('/keys'), get<{ connectors: Connector[] }>('/connectors')]);
  keys = k.keys;
  connectors = c.connectors;
}

const STATUS_TONE = { active: 'positive', revoked: 'neutral', expired: 'warning' } as const;

function access(k: ApiKey): TemplateResult {
  const parts = Object.entries(k.grants).filter(([, s]) => s.length);
  if (!parts.length) return html`<span class="muted">No access</span>`;
  return html`${parts.map(([name, scopes]) => {
    const c = connectors.find((x) => x.name === name);
    const labels = scopes.map((s) => c?.scopes.find((d) => d.id === s)?.label ?? s);
    return html`<div><b>${c?.display_name ?? name}</b>: <span class="muted">${labels.join(', ')}</span></div>`;
  })}`;
}

/** Scope checkboxes for every connector, pre-ticked from `grants`. */
function grantFields(grants: Record<string, string[]>): TemplateResult {
  return html`${connectors.map(
    (c) => html`<fieldset class="connector"><legend>${c.display_name}</legend>
      <div class="scopes">${c.scopes.map(
        (s) => html`<label><input type="checkbox" name="scope" value="${c.name}:${s.id}" ?checked=${grants[c.name]?.includes(s.id) ?? false}>
          <b>${s.label}</b><span class="muted">${s.description}</span></label>`,
      )}</div></fieldset>`,
  )}`;
}

function readGrants(form: HTMLFormElement): Record<string, string[]> {
  const grants: Record<string, string[]> = {};
  for (const el of form.querySelectorAll<HTMLInputElement>('input[name=scope]:checked')) {
    const [name, scope] = [el.value.slice(0, el.value.indexOf(':')), el.value.slice(el.value.indexOf(':') + 1)];
    (grants[name] ??= []).push(scope);
  }
  return grants;
}

const expiryIso = (date: string) => (date ? new Date(`${date}T23:59:59`).toISOString() : null);

function revealModal(key: string, k: ApiKey): () => TemplateResult {
  return () => html`<div class="modal-backdrop"><div class="modal wide" role="dialog" aria-modal="true" aria-labelledby="reveal-title">
    <h2 id="reveal-title">Copy the key for “${k.name}” now</h2>
    <p>This is the only time it is shown. Only a fingerprint is stored, so if it's lost, create a new key and revoke this one.</p>
    <div class="key-reveal"><code>${key}</code><button class="btn primary" @click=${() => copy(key, 'Key copied')}>Copy</button></div>
    <p>Callers send it in the <code>x-api-key</code> header, e.g. in a MoEngage connector campaign: header <b>x-api-key</b>, value: this key.</p>
    ${k.mode === 'test' ? html`<div class="banner info">Test key: every Stannp send made with it is rendered as a PDF but never printed or posted.</div>` : ''}
    <div class="actions"><button class="btn" @click=${closeModal}>I've stored it safely</button></div>
  </div></div>`;
}

function keyForm(existing: ApiKey | null): () => TemplateResult {
  const submit = async (e: Event) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const v = Object.fromEntries(new FormData(form)) as Record<string, string>;
    const grants = readGrants(form);
    if (existing) {
      const ok = await act(
        () => api('PATCH', `/keys/${existing.id}`, { name: v.name, description: v.description || null, grants, expires_at: expiryIso(v.expires ?? '') }),
        'Key updated',
        load,
      );
      if (ok) closeModal();
      return;
    }
    const r = await act(
      () => api<{ key: string; api_key: ApiKey }>('POST', '/keys', { name: v.name, description: v.description || undefined, mode: v.mode, grants, expires_at: expiryIso(v.expires ?? '') }),
      null,
      load,
    );
    if (r) openModal(revealModal(r.key, r.api_key));
  };
  return () => html`<div class="modal-backdrop" @click=${(e: Event) => e.target === e.currentTarget && closeModal()}>
    <div class="modal wide" role="dialog" aria-modal="true" aria-labelledby="key-title">
      <h2 id="key-title">${existing ? `Edit “${existing.name}”` : 'New API key'}</h2>
      <form class="form-stack" @submit=${submit}>
        <label class="field">Name<input name="name" required maxlength="80" placeholder="e.g. MoEngage welcome letters" .value=${existing?.name ?? ''}></label>
        <label class="field">Notes (optional)<input name="description" maxlength="500" placeholder="Who uses it, where it is stored" .value=${existing?.description ?? ''}></label>
        ${existing
          ? html`<p>Mode: ${existing.mode === 'live' ? pill('Live', 'accent') : pill('Test', 'info')} <span class="muted">(fixed when the key is created)</span></p>`
          : html`<div class="radios">
              <label class="radio"><input type="radio" name="mode" value="test" checked><b>Test</b><span class="muted">Vendor test mode always: Stannp renders a PDF, nothing is printed or posted. Use this while setting up.</span></label>
              <label class="radio"><input type="radio" name="mode" value="live"><b>Live</b><span class="muted">Real calls: letters are printed, posted and charged.</span></label>
            </div>`}
        <div><span class="label">Access</span>${grantFields(existing?.grants ?? {})}</div>
        <label class="field">Expires (optional)<input type="date" name="expires" .value=${existing?.expires_at ? existing.expires_at.slice(0, 10) : ''}></label>
        <div class="actions"><button type="button" class="btn quiet" @click=${closeModal}>Cancel</button><button class="btn primary">${existing ? 'Save' : 'Create key'}</button></div>
      </form>
    </div></div>`;
}

export const keysPage: Page = {
  title: 'API keys',
  load,
  view() {
    const admin = isAdmin();
    const revoke = async (k: ApiKey) => {
      const ok = await confirmDialog({
        title: `Revoke “${k.name}”?`,
        body: 'Calls with this key stop working straight away. This cannot be undone; create a new key to restore access.',
        confirmLabel: 'Revoke key',
        tone: 'danger',
      });
      if (ok) await act(() => api('POST', `/keys/${k.id}/revoke`), 'Key revoked', load);
    };
    const shown = keys.filter((k) => showRevoked || k.status !== 'revoked');
    return html`
      <div class="row-between page-head">
        <h1>API keys</h1>
        ${admin ? html`<button class="btn primary" @click=${() => openModal(keyForm(null))}>New API key</button>` : ''}
      </div>
      <p class="hint">Each internal system gets its own key, allowed only the connectors and actions it needs. Keys are sent in the
        <code>x-api-key</code> header and are shown once, when created.</p>
      <label class="check mb-4"><input type="checkbox" .checked=${showRevoked} @change=${(e: Event) => {
        showRevoked = (e.target as HTMLInputElement).checked;
        app.rerender();
      }}> Show revoked keys</label>
      <div class="table-wrap"><table>
        <thead><tr><th>Key</th><th>Mode</th><th>Access</th><th>Last used</th><th>Created</th><th>Status</th>${admin ? html`<th></th>` : ''}</tr></thead>
        <tbody>${shown.length
          ? shown.map(
              (k) => html`<tr>
                <td><b>${k.name}</b><br><code class="id">${k.preview}</code>${k.description ? html`<br><span class="muted">${k.description}</span>` : ''}</td>
                <td>${k.mode === 'live' ? pill('Live', 'accent') : pill('Test', 'info')}</td>
                <td>${access(k)}</td>
                <td>${ago(k.last_used_at)}</td>
                <td>${when(k.created_at)}<br><span class="muted">${k.created_by}</span></td>
                <td>${pill(k.status, STATUS_TONE[k.status])}${k.expires_at && k.status === 'active' ? html`<br><span class="muted">until ${when(k.expires_at)}</span>` : ''}</td>
                ${admin
                  ? html`<td class="nowrap">${k.status === 'revoked'
                      ? ''
                      : html`<button class="btn quiet compact" @click=${() => openModal(keyForm(k))}>Edit</button>
                          <button class="btn destructive outline compact" @click=${() => revoke(k)}>Revoke</button>`}</td>`
                  : ''}
              </tr>`,
            )
          : emptyRow(admin ? 7 : 6, 'No API keys yet.')}</tbody>
      </table></div>`;
  },
};
